import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { ButtonDeleteIds } from 'components/ButtonDeleteIds';
import { TableTopPanel } from 'components/TableTopPanel';
import { AG_GRID_ICON_SETUP } from 'constantsProject';
import type { BimChapterEnum, BimPriceLevelEnum, WorkOrderChangeStatusEnum } from 'gql/graphql';
import { useHighLightRulesWithRequestData } from 'pages/Project/Common/hooks/useHighLightRulesWithRequestData';

import type { RowClickedEvent } from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';
import type { ForwardedRef } from 'react';
import { forwardRef, useCallback, useMemo } from 'react';

import { BimWorkOrderPriceRowDetails } from '../../BimCommonComponents/BimWorkOrderPriceRowDetails';
import { getBimWorkOrderRowClass } from '../../WorkOrder/BimWorkOrderPriceTable/cellRenderers';
import { BimWorkOrderRowTypeEnum } from '../../WorkOrder/types';
import { useBimWorkOrderChangePriceStates } from '../BimWorkOrderChange/hooks';
import { useBimWorkOrderChangeTotals } from '../hooks';

import {
    useBimWorkOrderChangeColumns,
    useBimWorkOrderChangeDeletePrices,
    useBimWorkOrderChangeDetailsColumns,
    useBimWorkOrderChangePriceData,
    useBimWorkOrderChangePriceDetailsData,
} from './hooks';

const defaultColDef = {
    resizable: true,
    wrapHeaderText: true,
    spanHeaderHeight: true,
};

interface IBimWOrkOrderChangePriceTable {
    workOrderChangeId: number;
    chapter: BimChapterEnum;
    workOrderRootObjectId: number | undefined;
    workOrderChangeRootObjectId: number | undefined;
    constructionTypeId: number | undefined;
    workOrderChangeStatus: WorkOrderChangeStatusEnum | undefined;
    makeRefreshTable: () => void;
    priceLevel: BimPriceLevelEnum | undefined;
}

export const BimWorkOrderChangePriceTable = forwardRef(
    (props: IBimWOrkOrderChangePriceTable, ref: ForwardedRef<AgGridReact>) => {
        const {
            workOrderChangeId,
            chapter,
            workOrderRootObjectId,
            workOrderChangeStatus,
            workOrderChangeRootObjectId,
            constructionTypeId,
            makeRefreshTable: makeRefresh,
            priceLevel,
        } = props;

        const tableKey = useMemo(
            () => `${chapter}_${workOrderRootObjectId}-${priceLevel}`,
            [chapter, workOrderRootObjectId, priceLevel],
        );

        const {
            bimWorkOrderChangePriceStatesData: { hasExcessCompletedTotalSMR },
            bimWorkOrderChangePriceStatesMakeRequest,
        } = useBimWorkOrderChangePriceStates({
            workOrderChangeId,
            chapter,
            workOrderObjectId: workOrderRootObjectId,
            workOrderChangeObjectId: workOrderChangeRootObjectId,
        });

        const makeRefreshTable = useCallback(() => {
            makeRefresh();
            bimWorkOrderChangePriceStatesMakeRequest();
        }, [bimWorkOrderChangePriceStatesMakeRequest, makeRefresh]);

        const { dataSource, totalItems, fetchedItems, changeFilterHandler } = useBimWorkOrderChangePriceData({
            workOrderChangeId,
            chapter,
            workOrderRootObjectId,
            workOrderChangeRootObjectId,
            priceLevel,
        });

        const {
            columns,
            checkedRows,
            resetCheckedRows,
            checkedDetailsRow,
            handlerCloseDetails,
            detailsType,
            detailsRowType,
            fullSetSettingsColumns,
            isColumnVisible,
            tableSettingsConfirm,
        } = useBimWorkOrderChangeColumns({
            changeFilterHandler,
            workOrderChangeId,
            workOrderRootObjectId,
            workOrderChangeRootObjectId,
            chapter,
            workOrderChangeStatus,
            hasExcessCompletedTotalSMR,
            refreshTable: makeRefresh,
        });

        const { deleteSetData, deleteStatus, deleteError, deleteData, deleteHandler } =
            useBimWorkOrderChangeDeletePrices({
                workOrderChangeId,
                checkedRows,
            });

        const { detailsData, detailsBaseRow, isDetailsLoading, rowRevisionDataSource, detailsTitle } =
            useBimWorkOrderChangePriceDetailsData({
                chapter,
                checkedPriceRow: checkedDetailsRow,
                detailsType,
                detailsRowType,
                workOrderChangeId,
                workOrderRootObjectId,
                workOrderChangeRootObjectId,
                constructionTypeId,
                priceLevel,
            });

        /*
         * данные для строки итогов
         */
        const { rowTotalsData } = useBimWorkOrderChangeTotals({
            workOrderChangeId,
            chapter,
            workOrderObjectId: workOrderRootObjectId,
            workOrderChangeObjectId: workOrderChangeRootObjectId,
            constructionTypeId,
            priceLevel,
        });

        const { detailsColumns } = useBimWorkOrderChangeDetailsColumns({
            baseColumns: columns,
            detailsType,
            detailsRowType,
            chapter,
        });

        const tableApi = ref && 'current' in ref ? ref.current : undefined;

        const refreshKey = useMemo(
            () => `${workOrderChangeId}_${workOrderChangeStatus}`,
            [workOrderChangeId, workOrderChangeStatus],
        );

        const { rules, rowClickHandler, getTableDataId } = useHighLightRulesWithRequestData({
            requestIds: useMemo(() => ['bimWorkOrderChangeUploadPriceFiles'], []),
            dataKeyAccessor: 'itemIds',
            baseRules: getBimWorkOrderRowClass,
            gridApiRef: tableApi,
            refreshKey,
        });

        const handleRowClick = useCallback(
            (e: RowClickedEvent) => {
                rowClickHandler(e?.data?.id);
            },
            [rowClickHandler],
        );

        const gridOptions = useMemo(
            () => ({
                onRowClicked: handleRowClick,
            }),
            [handleRowClick],
        );

        const onDeleteSuccess = useCallback(() => {
            makeRefreshTable();
            resetCheckedRows();
        }, [makeRefreshTable, resetCheckedRows]);

        return (
            <>
                <TableTopPanel
                    checkedRowsArr={checkedRows}
                    resetCheckedRows={resetCheckedRows}
                    totalItems={totalItems}
                    fetchedItems={fetchedItems}
                    optionalColumns={fullSetSettingsColumns}
                    handleTableSettingsConfirm={tableSettingsConfirm}
                    isColumnVisible={isColumnVisible}
                    areChildrenOnLeftSide
                    showPinColumn
                    tableApi={tableApi}
                >
                    <ButtonDeleteIds
                        deleteData={deleteData}
                        deleteStatus={deleteStatus}
                        deleteError={deleteError}
                        deleteSetData={deleteSetData}
                        handleDelete={deleteHandler}
                        onSuccess={onDeleteSuccess}
                        textHeader="Удаление ЛСР"
                        textConfirmation="Вы уверены, что хотите удалить записи?"
                        style={{ marginRight: 10 }}
                    />
                </TableTopPanel>
                <div className="ag-theme-alpine BimWorkOrderPrice customHeaderColor">
                    <AgGridTable
                        ref={ref}
                        datasource={dataSource}
                        pinnedBottomRowData={rowTotalsData}
                        columnDefs={columns}
                        rowModelType="infinite"
                        tooltipShowDelay={200}
                        maxConcurrentDatasourceRequests={1}
                        maxBlocksInCache={50}
                        cacheBlockSize={50}
                        defaultColDef={defaultColDef}
                        icons={AG_GRID_ICON_SETUP}
                        suppressDragLeaveHidesColumns
                        gridOptions={gridOptions}
                        rowClassRules={rules}
                        getRowId={getTableDataId}
                        context={{
                            tableUniqueId: `${chapter}_${workOrderRootObjectId}_${workOrderChangeRootObjectId}`,
                        }}
                        key={tableKey}
                    />
                </div>

                {checkedDetailsRow && (
                    <BimWorkOrderPriceRowDetails
                        modalTitle={detailsTitle}
                        datasource={rowRevisionDataSource}
                        baseRow={detailsBaseRow}
                        data={detailsData}
                        columns={detailsColumns}
                        onClose={handlerCloseDetails}
                        isDetailsLoading={isDetailsLoading}
                        defaultColDef={defaultColDef}
                        styleClass="BimWorkOrderPriceDetails customHeaderColor"
                        rowClassRules={getBimWorkOrderRowClass}
                        priceLevel={detailsRowType === BimWorkOrderRowTypeEnum.TotalsString ? undefined : priceLevel}
                    />
                )}
            </>
        );
    },
);

BimWorkOrderChangePriceTable.displayName = 'BimWorkOrderChangePriceTable';
